<script lang="ts" setup>
import Swal from "sweetalert2";
import { usePelniStore } from "~/stores/pelni";
import type { Pelabuhan } from "~/istypes";

const router = useRouter();

const pelniStore = usePelniStore();
const localStorageStore = useLocalStorageStore();

const pelabuhanList = ref<Pelabuhan[]>([]);
const selectedPelabuhanAsal = ref<Pelabuhan | null>(null);
const selectedPelabuhanTujuan = ref<Pelabuhan | null>(null);
const waktuBerangkat = ref("");
const dewasaPria = ref(1);
const dewasaWanita = ref(1);
const bayi = ref(0);
const trip = ref("O");
const searchTerm = ref("");
const loading = ref(false);
const orgInput = ref();
const desInput = ref();

const checkSchedule = async () => {
  try {
    loading.value = true;
    const cariTiket = {
      selectedPelabuhanAsal: selectedPelabuhanAsal.value,
      selectedPelabuhanTujuan: selectedPelabuhanTujuan.value,
      waktuBerangkat: waktuBerangkat.value,
      dewasaPria: dewasaPria.value,
      dewasaWanita: dewasaWanita.value,
      bayi: bayi.value,
      trip: trip.value,
    };
    localStorage.setItem("formCariTiket", JSON.stringify(cariTiket));
    
    const response = await pelniStore.submitTiketPelni({
      trip: "O",
      org: selectedPelabuhanAsal.value?.code?.toString(),
      des: selectedPelabuhanTujuan.value?.code?.toString(),
      tgl_dep: new Date(waktuBerangkat.value).toISOString().split("T")[0],
      adt_m: dewasaPria.value || "0",
      adt_f: dewasaWanita.value || "0",
      inf: bayi.value || "0",
    });

    if (response?.status?.code === 200) {
      localStorage.setItem("schedulePelni", JSON.stringify(response));
      navigateTo('/pelni/schedule')
    } else if (response?.status?.code === 400) {
      Swal.fire({
        title: `${response.status.message}`,
        icon: "warning",
      });
    } else {
      Swal.fire({
        title: "Error",
        text: "Failed to get schedule information.",
        icon: "error",
      });
    }
  } catch (error) {
    console.error("Terjadi kesalahan! :", error);
  }finally{
    loading.value = false;
  }
};

const filteredPelabuhanList = computed(() => {
  const lowerSearchTerm = searchTerm.value.toLowerCase();
  return pelabuhanList.value.filter(
    (pelabuhan) =>
      pelabuhan &&
      pelabuhan.name &&
      pelabuhan.name.toLowerCase().includes(lowerSearchTerm)
  );
});

const isSelected = (
  pelabuhan: Pelabuhan,
  selectedPelabuhan: Pelabuhan | null
): boolean => {
  return (
    selectedPelabuhan !== null && selectedPelabuhan.code === pelabuhan.code
  );
};

const selectPelabuhan = (
  pelabuhan: Pelabuhan,
  type: "asal" | "tujuan"
): void => {
  if (pelabuhan && pelabuhan.code !== undefined) {
    if (type === "asal") {
      selectedPelabuhanAsal.value = pelabuhan;
      searchTerm.value = "";
    } else if (type === "tujuan") {
      selectedPelabuhanTujuan.value = pelabuhan;
      searchTerm.value = "";
    }
  } else {
    console.error("Invalid pelabuhan id:", pelabuhan);
  }
};


const focusSearch = (type: any) => {
  nextTick(() => {
    let element = null;
    if (type === 'org') {
      element = orgInput.value;
    } else if (type === 'des') {
      element = desInput.value;
    }
    
    if (element) {
      setTimeout(() => {
        element.focus();
      }, 100); // Adjust the delay if necessary
    } else {
      console.error(`Element with ref ${type} not found`);
    }
  });
};

watch(
  [() => selectedPelabuhanAsal.value, () => selectedPelabuhanTujuan.value],
  () => {
    // Update your logic when selectedPelabuhanAsal or selectedPelabuhanTujuan changes
  }
);

const fetchPelabuhanList = () => {
  try {
    // Implement your logic to fetch the pelabuhanList from the store
    const response = localStorageStore.getHarbours();
    if (response && Array.isArray(response)) {
      pelabuhanList.value = response.map((item: Pelabuhan) => ({
        code: item.code,
        name: item.name,
      }));
    } else {
      console.error("Invalid response or response is not an array");
    }
  } catch (error) {
    console.error("Terjadi kesalahan! :", error);
  }
};

watch([selectedPelabuhanAsal, selectedPelabuhanTujuan], () => {
  fetchPelabuhanList();
});

onMounted(() => {
  fetchPelabuhanList();
});

const swapValues = () => {
  const temp = { ...selectedPelabuhanAsal.value };
  selectedPelabuhanAsal.value = { ...selectedPelabuhanTujuan.value };
  selectedPelabuhanTujuan.value = { ...temp };
};
</script>
<template>
  <Customloading v-if="loading" />
  <div>
      <div class="">
        <div class="form mx-2 my-3">
          <div class="row mb-3">
            <!-- Pelabuhan Asal -->
            <div class="col-12 col-lg-8 col-md-8">
              <div class="d-flex d-flex-sm">
                <div class="col">
                  <label for="">Dari</label>
                  <div class="dropdown input-group mb-3">
                    <button
                      class="form-select rounded text-start"
                      type="button"
                      id="dropdownPelabuhanAsal"
                      data-bs-toggle="dropdown"
                      aria-expanded="false"
                      @click="focusSearch('org')"
                    >
                      {{
                        selectedPelabuhanAsal
                          ? selectedPelabuhanAsal.name
                          : "Pilih Pelabuhan Asal"
                      }}
                    </button>
                    <ul
                      class="dropdown-menu pt-0 full-width"
                      aria-labelledby="dropdownPelabuhanAsal"
                      style="width: 17rem; position: relative"
                    >
                      <div class="bg-white sticky-top py-1">
                        <input
                          type="text"
                          class="form-control border border-1 rounded shadow-none m-2"
                          style="max-width: calc(100% - 16px); top: 0"
                          placeholder="Search..."
                          v-model="searchTerm"
                          ref="orgInput"
                          autocomplete="off"
                        />
                      </div>
                      <div class="scrollable-menu ms-2">
                        <li
                          v-for="pelabuhan in filteredPelabuhanList"
                          :key="pelabuhan.code"
                          class="list-group-item px-2"
                          :class="{
                            active: isSelected(pelabuhan, selectedPelabuhanAsal),
                          }"
                          @click="selectPelabuhan(pelabuhan, 'asal')"
                          style="cursor: pointer; max-width: 250px"
                        >
                          {{ pelabuhan.name }}
                        </li>
                      </div>
                    </ul>
                  </div>
                </div>
                <div class="mt-4 mx-2 text-center-sm mt-0-sm">
                  <div class="swap-button">
                    <button class="btn btn-primary" @click="swapValues">
                      <i class="fa-solid fa-repeat"></i>
                    </button>
                  </div>
                </div>
                <!-- Pelabuhan Tujuan -->
                <div class="col">
                  <label for="">Ke</label>
                  <div class="dropdown input-group mb-3">
                    <button
                      class="form-select rounded text-start"
                      type="button"
                      id="dropdownPelabuhanTujuan"
                      data-bs-toggle="dropdown"
                      aria-expanded="false"
                      @click="focusSearch('des')"
                    >
                      {{
                        selectedPelabuhanTujuan
                          ? selectedPelabuhanTujuan.name
                          : "Pilih Pelabuhan Tujuan"
                      }}
                    </button>
                    <ul
                      class="dropdown-menu pt-0 full-width"
                      aria-labelledby="dropdownPelabuhanTujuan"
                      style="width: 17rem; position: relative"
                    >
                      <div class="bg-white sticky-top py-1">
                        <input
                          type="text"
                          class="form-control border border-1 rounded shadow-none m-2"
                          style="max-width: calc(100% - 16px); top: 0"
                          placeholder="Search..."
                          v-model="searchTerm"
                          ref="desInput"
                          autocomplete="off"
                        />
                      </div>
                      <div class="scrollable-menu ms-2">
                        <li
                          v-for="pelabuhan in filteredPelabuhanList"
                          :key="pelabuhan.code"
                          class="list-group-item px-2"
                          :class="{
                            active: isSelected(pelabuhan, selectedPelabuhanTujuan),
                          }"
                          @click="selectPelabuhan(pelabuhan, 'tujuan')"
                          style="cursor: pointer; max-width: 250px"
                        >
                          {{ pelabuhan.name }}
                        </li>
                      </div>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
            <div class="col-12 col-lg-4 col-md-4 ">
              <label for="">Jumlah Penumpang</label>
              <div class="dropdown input-group mb-3">
                <button
                  class="form-select rounded text-start"
                  type="button"
                  id="dropdownPenumpang"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                  style="width: calc(100% - 20px); top: 0; overflow: hidden;"
                >
                  {{ `${dewasaPria + dewasaWanita} Dewasa`}}<label v-if="bayi > 0">, {{ `${bayi} Bayi` }}</label>
                </button>
                <ul
                  class="dropdown-menu pt-0 full-width-sm"
                  aria-labelledby="dropdownPenumpang"
                  @pointerdown.stop
                  @click.stop
                >
                  <div class="mx-2 my-3">
                    <div class="row mt-3">
                      <div class="col col-lg-7 col-md-7">
                        <label>Dewasa Pria</label><br>
                        <small class="pb-3" style="font-size: 10px;">(diatas 2 tahun)</small>
                      </div>
                      <div class="col col-lg-5 col-md-5">
                        <InputNumber v-model="dewasaPria" :min="0" :max="4" />
                      </div>
                    </div>
                    <div class="row mt-3">
                      <div class="col col-lg-7 col-md-7">
                        <label>Dewasa Wanita</label><br>
                        <small class="pb-3" style="font-size: 10px;">(diatas 2 tahun)</small>
                      </div>
                      <div class="col col-lg-5 col-md-5">
                        <InputNumber v-model="dewasaWanita" :min="0" :max="4" />
                      </div>
                    </div>
                    <div class="row mt-3">
                      <div class="col col-lg-7 col-md-7">
                        <label>Bayi</label><br>
                        <small class="pb-3" style="font-size: 10px;">(dibawah 2 tahun)</small>
                      </div>
                      <div class="col col-lg-5 col-md-5">
                        <InputNumber v-model="bayi" :min="0" :max="4" />
                      </div>
                    </div>
                  </div>
                </ul>
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col-12 col-md-3 col-lg-3 mb-3">
              <label for="">Berangkat</label>
              <dateInput v-model="waktuBerangkat"  />
            </div>
            <div class="col-12 col-md-3 col-lg-3">
              <button
                @click="checkSchedule"
                type="button"
                class="btn fw-semibold text-white mt-4"
                style="width: 100%; background-color: #fb8b34"
              >
                Cari Tiket
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
</template>
<style scoped>
.scrollable-menu {
  max-height: 200px;
  overflow: auto;
}
</style>